import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SelectForm from "../SelectForm/SelectForm";
import InputForm from "../InputForm/InputForm";
import { formatData } from "../../utils/helpers";
import useCoinDetails from "../../hooks/useCoinDetails";
import deleteIcon from "../../assets/icons/delete_icon.svg";
import { customToast as toast } from "../../utils";
import { ECurrencyCode } from "../../Enums/Coin";

function DiscountsEditorFields({
  productTotal,
  defaultDiscounts,
  discountsList,
  onDiscountsListChange,
}) {
  const { currentSymbolCoin, currentCoinCode, convertToCurrency } =
    useCoinDetails();
  const [cont, setCont] = useState(false);
  const [discount, setDiscount] = useState({
    Type: "1",
    Amount: "",
  });
  const [isComponentHydrated, setIsComponentHydrated] = useState(false);
  const keyCounter = useRef(1);
  const totalPrice = convertToCurrency({
    amount: productTotal,
    fromCurrencyCode: ECurrencyCode.DOLLAR,
    toCurrencyCode: currentCoinCode,
  });
  const theme = localStorage.getItem("theme");
  const types = [
    { Name: "Porcentaje", Id: "1" },
    { Name: "Monto", Id: "2" },
  ];

  const colorLine = theme === "light" ? "#030710" : "#fff";

  const handleDelete = line => {
    const filteredList = discountsList.filter(item => item !== line);
    onDiscountsListChange(
      filteredList.map((item, i) => ({ ...item, key: i + 1 })),
    );
    keyCounter.current = filteredList.length + 1;
    if (cont === true) {
      setCont(false);
    }
  };

  const validateTotalDiscounts = () => {
    if (discountsList.length === 5) {
      setCont(true);
    }
  };

  const clear = () => {
    setDiscount({
      Type: "1",
      Amount: "",
    });
  };
  const handleChange = (name, value) => {
    setDiscount(prev => ({ ...prev, [name]: value }));
  };
  const getCurrentTotalDiscount = () => {
    let totalsAmount = 0;
    discountsList.forEach(item => {
      totalsAmount += parseFloat(item.Percent, 10);
    });
    return totalsAmount;
  };
  const getDiscountAmountFromCleanTotal = (discountPercentage = 0) => {
    if (!discountPercentage) return 0;
    const amountToDiscount = totalPrice * (discountPercentage / 100);
    return amountToDiscount;
  };

  const replaceFullDiscountsList = (newDiscountsDataList = []) => {
    let localKeyCounter = 1;
    const finalDiscountsDataList = newDiscountsDataList.map(discountData => {
      const finalDiscountDataObj = {
        Percent: discountData.discountPercentage,
        Amount: discountData.amountToDiscount,
        key: localKeyCounter,
      };
      localKeyCounter += 1;

      return finalDiscountDataObj;
    });
    keyCounter.current = localKeyCounter;
    onDiscountsListChange(finalDiscountsDataList);
  };

  const addPercentageDiscount = (discountPercentage = 0) => {
    if (!discountPercentage) return;
    const totalDiscount = getCurrentTotalDiscount();
    const amount = getDiscountAmountFromCleanTotal(discountPercentage);
    if (parseFloat(discountPercentage, 10) + totalDiscount > 100) {
      toast.error(
        "La suma de los montos de los descuentos exceden el precio unitario.",
      );
    } else {
      const currentKey = keyCounter.current;
      onDiscountsListChange(discounts => [
        ...discounts,
        {
          Percent: discountPercentage,
          Amount: amount,
          key: currentKey,
        },
      ]);
      keyCounter.current += 1;
      clear();
      validateTotalDiscounts();
    }
  };
  const addFixedAmountDiscount = (discountAmount = 0) => {
    if (!discountAmount) return;
    const totalDiscount = getCurrentTotalDiscount();
    const percent = (parseFloat(discountAmount) * 100) / totalPrice;
    if (percent > 100) {
      toast.error("El monto no puede exceder el precio unitario del producto");
    } else {
      const amount = (totalPrice * percent) / 100;

      if (percent + totalDiscount > 100) {
        toast.error(
          "La suma de los montos de los descuentos exceden el precio unitario.",
        );
      } else {
        const currentKey = keyCounter.current;
        onDiscountsListChange(discounts => [
          ...discounts,
          {
            Amount: amount.toFixed(3),
            Percent: percent.toFixed(1),
            key: currentKey,
          },
        ]);
        keyCounter.current += 1;
        clear();
        validateTotalDiscounts();
      }
    }
  };
  const handleAdd = () => {
    if (discount.Type === "" || discount.Amount === "") {
      toast.error("Debe completar todos los datos requeridos");
    } else if (discount.Amount === 0) {
      toast.error("El valor proporcionado debe ser mayor a 0");
    } else if (
      discount.Type === "1" &&
      (discount.Amount === 0 || discount.Amount > 100)
    ) {
      toast.error("El valor del porcentaje debe ser entre 0 y 100");
    } else {
      if (discount.Type === "1") {
        addPercentageDiscount(discount.Amount);
        return;
      }
      addFixedAmountDiscount(discount.Amount);
    }
  };

  useEffect(() => {
    setIsComponentHydrated(true);
  }, []);

  useEffect(
    () => {
      if (!isComponentHydrated) return;
      replaceFullDiscountsList(
        defaultDiscounts
          .map(discountPercentage => ({
            amountToDiscount:
              getDiscountAmountFromCleanTotal(discountPercentage),
            discountPercentage,
          }))
          .filter(discountData => discountData.amountToDiscount > 0),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isComponentHydrated],
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: "1rem",
        flexDirection: "column",
      }}
    >
      <SelectForm
        color="secondary"
        data={formatData(types, "Name")}
        label="Seleccione:"
        name="Type"
        size="small"
        value={discount?.Type}
        onChange={e => handleChange(e.target.name, e.target.value)}
      />
      <InputForm
        color="secondary"
        id="Id"
        label="Valor"
        name="Amount"
        size="small"
        type="number"
        value={discount?.Amount}
        variant="filled"
        onChange={e => handleChange(e.target.name, e.target.value)}
      />
      <div className="content-flex-end">
        <Button
          color="secondary"
          disabled={cont}
          size="medium"
          sx={{ width: "50%" }}
          type="Button"
          variant="contained"
          onClick={handleAdd}
        >
          Agregar Descuento
        </Button>
      </div>
      <div className="data-table-container">
        <TableContainer
          sx={{
            overflow: "auto",
            height: "124px",
            border: `2px solid  ${colorLine}`,
            paddingY: "0.2rem",
            paddingX: "1rem",
            borderRadius: "4px",
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                align="center"
                sx={{
                  borderTop: "none",
                  borderBottom: `2px solid  ${colorLine}`,
                  borderRight: "none",
                  background: "transparent",
                  height: "1rem",
                  paddingTop: 0,
                  paddingBottom: "0.1rem",
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  #
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Porcentaje
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Monto
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Eliminar
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {discountsList?.map(line => (
                <TableRow
                  key={line.key}
                  align="center"
                  sx={{
                    borderTop: "none",
                    borderBottom: `2px solid  ${colorLine}`,
                    borderRight: "none",
                    background: "transparent",
                    height: "1rem",
                    paddingTop: 0,
                    paddingBottom: "0.1rem",
                  }}
                >
                  <TableCell component="th" scope="row">
                    {line.key}
                  </TableCell>
                  <TableCell>{Number(line?.Percent).toFixed(3)}%</TableCell>
                  <TableCell>
                    {currentSymbolCoin}
                    {Number(line?.Amount).toFixed(3)}
                  </TableCell>
                  <TableCell>
                    <button
                      className="button-modifications"
                      type="button"
                      onClick={() => handleDelete(line)}
                    >
                      <img
                        alt="eliminar"
                        className=" icon-color"
                        src={deleteIcon}
                      />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Box>
  );
}

DiscountsEditorFields.propTypes = {
  productTotal: PropTypes.number.isRequired,
  discountsList: PropTypes.array.isRequired,
  defaultDiscounts: PropTypes.array.isRequired,
  onDiscountsListChange: PropTypes.func.isRequired,
};
export default DiscountsEditorFields;
